import User from '@/datatypes/user';
import StripeSubscription from '@/datatypes/stripe/subscription';
import StripeSubscriptionUsage from '@/datatypes/stripe/subscription-usage';
import Customer from '@/datatypes/enterprise/billing-customer';
import EnterpriseLimits from '@/datatypes/enterprise/limits';
import moment from 'moment';

export default class Enterprise {
  public id: string;
  public name: string;
  public email: string;
  public owner: User;
  public quota: number;
  public quotaRenewal: string;
  public smsQuota: number;
  public identityRequestQuota: number;
  public seats: number;
  public allocated: number;
  public stripeSubscription: StripeSubscription;
  public stripeSubscriptionUsage: Array<StripeSubscriptionUsage>;
  public billingCustomer: Customer;
  public limits: EnterpriseLimits;
  public payg: boolean;
  public customerId: string;
  public paygIncorrect = false;

  constructor(options) {
    this.id = options.id;
    this.name = options.name;
    this.email = options.email;
    this.quota = options.quota;
    this.quotaRenewal = options.quotaRenewal;
    this.smsQuota = options.smsQuota;
    this.identityRequestQuota = options.identityRequestQuota;

    this.seats = options.seats;
    this.allocated = options.allocated;
    if (typeof options.limits === 'object') {
      this.limits = new EnterpriseLimits(options.limits);
    } else {
      this.limits = new EnterpriseLimits({});
    }
    this.payg = options.payg || false;
    if (typeof options.stripeSubscription === 'object') {
      this.stripeSubscription = new StripeSubscription(options.stripeSubscription || {});
    } else {
      this.stripeSubscription = new StripeSubscription({});
    }
    if (typeof options.billingCustomer === 'object') {
      this.billingCustomer = new Customer(options.billingCustomer);
    } else {
      this.billingCustomer = new Customer({});
    }
    if (typeof options.stripeSubscriptionUsage === 'object') {
      this.stripeSubscriptionUsage = options.stripeSubscriptionUsage.map(
        (usage) => new StripeSubscriptionUsage(usage),
      );
    } else {
      this.stripeSubscriptionUsage = [] as StripeSubscriptionUsage[];
    }

    if (typeof options.owner === 'object') {
      this.owner = new User(options.owner);
      this.customerId = this.owner.sCustomerId;
    } else {
      this.owner = new User({});
      this.customerId = '';
    }

    if (
      (this.stripeSubscription.plan && this.stripeSubscription.plan.usageType === 'metered')
      && this.payg === false
    ) {
      this.paygIncorrect = true;
    }
  }

  public canNewSubscription() {
    if (this.hasSubscription()) return false;

    const allowed = [
      'Free forever', 'Trial', 'API', 'Enterprise', 'Business', 'Business pro', 'Entrepreneur',
    ];

    return allowed.indexOf(this.owner.kindName) !== -1;
  }

  public canNewCustomer() {
    return (
      (typeof this.owner.sCustomerId === 'undefined')
      || (this.owner.sCustomerId === '')
      || (this.owner.sCustomerId === null)
    );
  }

  public hasSubscription() {
    return (
      (typeof this.owner.subscriptionId !== 'undefined')
      && (this.owner.subscriptionId !== null)
      && (this.owner.subscriptionId !== '')
    );
  }

  public subscriptionStartDiffers() {
    if (!this.hasSubscription()) return false;

    if (!this.owner.periodStart) return true;

    const ownerStart = moment(this.owner.periodStart).unix();
    const diff = Math.abs(this.stripeSubscription.currentPeriodStart - ownerStart);

    return (diff > 30);
  }

  public subscriptionEndDiffers() {
    if (!this.hasSubscription()) return false;

    if (!this.owner.currentPeriodEnd) return true;

    const ownerEnd = moment(this.owner.currentPeriodEnd).unix();
    const diff = Math.abs(this.stripeSubscription.currentPeriodEnd - ownerEnd);

    return (diff > 30);
  }
}
